import { z } from 'zod';

import {
  filterSchema,
  titleSchema,
} from '../../universal-widget/settingsSchema';

/**
 * What bubble size encodes (RSP-7292). `fixed` is the default: the chart is
 * purely positional until someone chooses a measure. The keys double as the
 * i18n keys under `dashboard.widgetSettings.fields.bubbleSizeOptions`, and the
 * chart's caption reads the same key, so the setting and the chart cannot
 * disagree on what a measure is called.
 */
export const bubbleSizeEncodings = [
  'fixed',
  'linkedControls',
  'inherentToResidualMovement',
  'childRisks',
] as const;

export type BubbleSizeEncoding = (typeof bubbleSizeEncodings)[number];

export const DEFAULT_BUBBLE_SIZE_ENCODING: BubbleSizeEncoding = 'fixed';

export const riskBubbleChartSettingsSchema = filterSchema.and(titleSchema).and(
  z.object({
    bubbleSize: z.enum(bubbleSizeEncodings).optional(),
  })
);

export type RiskBubbleChartSettings = z.infer<
  typeof riskBubbleChartSettingsSchema
>;

const isBubbleSizeEncoding = (value: unknown): value is BubbleSizeEncoding =>
  bubbleSizeEncodings.some((encoding) => encoding === value);

/**
 * The encoding a widget's stored settings ask for. A widget saved before the
 * setting existed, or one carrying a value this build no longer knows, draws
 * at the fixed size rather than failing.
 */
export const resolveBubbleSizeEncoding = (
  settings: { bubbleSize?: unknown } | null | undefined
): BubbleSizeEncoding => {
  const candidate = settings?.bubbleSize;

  return isBubbleSizeEncoding(candidate)
    ? candidate
    : DEFAULT_BUBBLE_SIZE_ENCODING;
};
